import React, { useState } from 'react';
import { useQuery } from 'wasp/client/operations';
import { getCurrentMealPlan } from 'wasp/client/operations';
import ShoppingListView from './ShoppingList.view';

// Business logic types
interface ShoppingItem {
  id: string;
  ingredient: string;
  quantity: number;
  unit: string;
  category: string;
  purchased: boolean;
  estimatedPrice?: number;
}

interface ShoppingList {
  id: string;
  items: ShoppingItem[];
  estimatedCost: number;
  status: string;
}

interface Household {
  id: string;
  name: string;
}

/**
 * ShoppingList Container - Centralizes all data logic, hooks, and business logic
 * 
 * This centralizes:
 * - Wasp operations and data fetching
 * - Shopping list state management
 * - View mode switching (personal vs household)
 * - Item checking/purchasing logic
 * - Cost calculations and data grouping
 * - All event handlers and business logic
 */
export default function ShoppingListContainer() {
  // Wasp hooks for data fetching
  const { data: currentMealPlan } = useQuery(getCurrentMealPlan, {});

  // Business logic state  
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState(false);

  // Business logic: process shopping list data
  const personalList = (currentMealPlan as any)?.shoppingList as ShoppingList | undefined;
  const groupedItems = personalList?.items?.reduce((acc: Record<string, ShoppingItem[]>, item: ShoppingItem) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, ShoppingItem[]>) || {};
  
  const totalCost = personalList?.items?.reduce(
    (sum: number, item: ShoppingItem) => sum + (item.estimatedPrice || 0), 
    0
  ) || 0;

  const shoppingList = personalList;

  // Business logic handlers
  const handleToggleItem = (itemId: string) => {
    setCheckedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleMarkAllPurchased = () => {
    setCheckedItems(new Set(shoppingList?.items?.map((item: ShoppingItem) => item.id) || []));
  };

  const handleToggleEditing = () => {
    setEditing(!editing);
  };

  const handleCompleteShopping = () => {
    // TODO: Implement complete shopping logic
    console.log('Complete shopping clicked');
  };

  const handleGoToMealPlans = () => {
    window.location.href = '/plans';
  };

  // Computed values
  const checkedItemsCount = checkedItems.size;
  const totalItemsCount = shoppingList?.items?.length || 0;
  const hasShoppingList = !!shoppingList;

  // Map all props to the view (simplified for personal shopping lists)
  const viewProps = {
    // Data
    shoppingList,
    groupedItems,
    totalCost,
    currentMealPlan,
    
    // State
    checkedItems,
    editing,
    
    // Computed values
    checkedItemsCount,
    totalItemsCount,
    hasShoppingList,
    
    // Event handlers
    onToggleItem: handleToggleItem,
    onMarkAllPurchased: handleMarkAllPurchased,
    onToggleEditing: handleToggleEditing,
    onCompleteShopping: handleCompleteShopping,
    onGoToMealPlans: handleGoToMealPlans,
  };

  return <ShoppingListView {...viewProps} />;
}

// Export types for the view component
export type { ShoppingItem, ShoppingList, Household };
